import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthContext } from "../../hoc/AuthContext";
import { getApiErrorMessage, jagranNextService, unwrapList } from "../../apiService/jagranNext.service";
import {
  EmptyState,
  formatWhen,
  inputClass,
  Notice,
  PAGE_SIZE,
  PageShell,
  Pager,
  Panel,
  registrationSummary,
  StatusBadge,
} from "./ui";

const STATUSES = ["", "lead", "pending_payment", "confirmed", "payment_failed"];

export default function Leads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedDomain } = useContext(AuthContext);
  const productId = selectedDomain?.id;
  const campaignId = searchParams.get("campaign_id") || "";
  const seasonId = searchParams.get("season_id") || "";
  const status = searchParams.get("status") || "";
  const offset = Number(searchParams.get("offset") || 0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!productId) return undefined;
    let active = true;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const response = await jagranNextService.listProductRegistrations(productId, {
          campaign_id: campaignId,
          season_id: seasonId,
          status,
          limit: PAGE_SIZE,
          offset,
        });
        if (!active) return;
        const list = unwrapList(response.data);
        setRows(list.rows);
        setTotal(list.total);
      } catch (requestError) {
        if (!active) return;
        setRows([]);
        setError(getApiErrorMessage(requestError));
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [productId, campaignId, seasonId, status, offset]);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("offset");
    setSearchParams(next);
  };

  return (
    <PageShell
      title="Leads"
      description="Registrations across campaigns for the selected product."
    >
      {!productId ? <Notice>Select a product in the header before loading leads.</Notice> : null}
      <Panel>
        <div className="mb-4">
          <label className="block max-w-xs text-sm font-semibold text-gray-700" htmlFor="lead-status">
            Status
            <select
              id="lead-status"
              className={`${inputClass} mt-1.5`}
              value={status}
              onChange={(event) => updateFilter("status", event.target.value)}
            >
              {STATUSES.map((item) => (
                <option key={item || "all"} value={item}>{item ? item.replace(/_/g, " ") : "All statuses"}</option>
              ))}
            </select>
          </label>
        </div>
        <Notice>{error}</Notice>
        {loading ? <p className="py-8 text-center text-sm text-gray-500">Loading leads...</p> : null}
        {!loading && productId && rows.length === 0 ? <EmptyState title="No leads found" /> : null}
        {rows.length > 0 ? (
          <>
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-3">Lead</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((registration) => (
                  <tr key={registration.id}>
                    <td className="px-3 py-3 text-gray-800">{registrationSummary(registration)}</td>
                    <td className="px-3 py-3"><StatusBadge value={registration.status} /></td>
                    <td className="px-3 py-3 text-gray-600">{formatWhen(registration.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager
              offset={offset}
              limit={PAGE_SIZE}
              count={rows.length}
              total={total}
              onPage={(nextOffset) => {
                const next = new URLSearchParams(searchParams);
                next.set("offset", String(nextOffset));
                setSearchParams(next);
              }}
            />
          </>
        ) : null}
      </Panel>
    </PageShell>
  );
}
